import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import gql from 'graphql-tag';

import UFOInteractionIgnore from '@atlaskit/react-ufo/interaction-ignore';

import { QuickFind as QuickFindInternal } from '@atlassian/search-page/quick-find';

import { useMutationInterop } from '@confluence/apollo-relay-interop';
import { TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { fg } from '@confluence/feature-gating';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	NAVIGATION_SHORTCUT,
	NAVIGATION_SLASH_SHORTCUT,
	UNIVERSAL_NAVIGATION_SHORTCUT_MAC,
	UNIVERSAL_NAVIGATION_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { getNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/getNavigateToTeamsApp.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { getPersistedGhostText } from './utils/getPersistedGhostText.ts';

const SEARCH_ROUTE_PRELOAD_DELAY = 500;

const QUICK_FIND_SEARCH_PERFORMED_EVENT_MUTATION = gql`
	mutation QuickFindSearchPerformedEventMutation(
		$cloudId: ID!
		$resultCount: Int!
		$query: String!
		$timestamp: String!
	) {
		confluence_generateSearchPerformedEvent(
			cloudId: $cloudId
			input: { resultCount: $resultCount, query: $query, timestamp: $timestamp }
		) {
			success
		}
	}
`;

const generateUserUrl = (userId: string, orgId: string) => {
	return fg('confluence-adopt-teams-nav-config')
		? getNavigateToTeamsApp(orgId, {
				type: 'USER',
				payload: {
					userId,
				},
			}).href
		: `/wiki/people/${userId}`;
};

const generateTeamUrl = (teamId: string, orgId: string) => {
	return fg('confluence-adopt-teams-nav-config')
		? getNavigateToTeamsApp(orgId, {
				type: 'TEAM',
				payload: {
					teamId,
				},
			}).href
		: `/wiki/people/team/${teamId}`;
};

const useTextParameter = createSingleQueryParamHook('text');

type SearchCompletedCallback = (resultCount?: number, query?: string) => void;

/**
 * Component that handles the search performed event mutation.
 * Wrapped in UFOInteractionIgnore to prevent the mutation from creating UFO holds.
 */
const SearchPerformedEventEmitter = ({
	cloudId,
	setHandleSearchCompletedRef,
}: {
	cloudId: string;
	setHandleSearchCompletedRef: (callback: SearchCompletedCallback) => void;
}) => {
	const [generateSearchPerformedEvent] = useMutationInterop<any>(
		// @ts-expect-error -- http://go/cfe-use-relay-artifacts
		QUICK_FIND_SEARCH_PERFORMED_EVENT_MUTATION,
	);

	const handleSearchCompleted = useCallback(
		(resultCount?: number, query?: string) => {
			if (!fg('should_enable_confluence_search_performed_event')) {
				return;
			}

			void generateSearchPerformedEvent({
				variables: {
					cloudId,
					resultCount: resultCount || 0,
					query: query || '',
					timestamp: new Date().toISOString(),
				},
			});
		},
		[cloudId, generateSearchPerformedEvent],
	);

	useEffect(() => {
		setHandleSearchCompletedRef(handleSearchCompleted);
	}, [handleSearchCompleted, setHandleSearchCompletedRef]);

	return null;
};

export const generateSearchUrl = (urlQueryParams: any) => {
	const url = new URL('/wiki/search', window.location.origin);
	const searchParams = new URLSearchParams(window.location.search);

	Object.entries(urlQueryParams).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			searchParams.set(key, String(value));
		} else {
			searchParams.delete(key);
		}
	});

	url.search = searchParams.toString();

	return url.href;
};

export const QuickFind = () => {
	const [open, setOpen] = useState(false);
	const {
		cloudId,
		orgId,
		activationId,
		isAdminHubAIEnabled,
		userId,
		displayName,
		isRovoLLMEnabled,
	} = useSessionData();
	const { push, preloadRoute } = useRouteActions();
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();
	const inputRef = useRef<HTMLInputElement>(null);

	const [persistedGhostText] = useState(() => getPersistedGhostText(inputRef));

	const onPersistGhostText = useCallback((newGhostText: string) => {
		if (fg('quickfind-ghosttext')) {
			confluenceLocalStorageInstance.setItem(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_PLATFORM_QUICKFIND_GHOSTTEXT,
				newGhostText,
			);
		}
	}, []);

	const textParameter = useTextParameter();
	const queryParams = useMemo(
		() => ({
			text: textParameter,
		}),
		[textParameter],
	);

	const analyticsAttributes = useMemo(
		() => ({ activationId, navdexPointType: TOUCH }),
		[activationId],
	);

	const user = useMemo(() => ({ id: userId, name: displayName }), [userId, displayName]);

	const experimentLayers = StatsigConfigurations.getStringList(
		'search_platform_layer_functions',
		[],
	);

	const handleSearchCompletedRef = useRef<SearchCompletedCallback>(() => {});
	const setHandleSearchCompletedRef = useCallback((callback: SearchCompletedCallback) => {
		handleSearchCompletedRef.current = callback;
	}, []);

	const onNavigate = useCallback((url: string) => push(url), [push]);
	const universalNavigationShortcut = isMac()
		? UNIVERSAL_NAVIGATION_SHORTCUT_MAC
		: UNIVERSAL_NAVIGATION_SHORTCUT_WIN;

	const onGenerateUserUrl = useCallback(
		(userIdParam: string) => generateUserUrl(userIdParam, orgId ?? ''),
		[orgId],
	);

	const onGenerateTeamUrl = useCallback(
		(teamIdParam: string) => generateTeamUrl(teamIdParam, orgId ?? ''),
		[orgId],
	);

	const callbacks = useMemo(
		() => ({
			onSearchCompleted: (resultCount?: number, query?: string) =>
				handleSearchCompletedRef.current(resultCount, query),
		}),
		[],
	);

	const onOpenQuickFind = useCallback(() => setOpen(true), []);

	const preloadedRef = useRef(false);
	const onGenerateSearchUrlWithPreload = useCallback(
		(urlQueryParams: any) => {
			const url = generateSearchUrl(urlQueryParams);

			if (!preloadedRef.current) {
				setTimeout(() => {
					const onIdle =
						window.requestIdleCallback ||
						window.requestAnimationFrame ||
						((cb) => setTimeout(cb, 0));
					onIdle(() => preloadRoute(url));
				}, SEARCH_ROUTE_PRELOAD_DELAY);
			}

			preloadedRef.current = true;

			return url;
		},
		[preloadRoute],
	);

	return (
		<>
			{/* Ignoring hold created by Apollo Mutation as the mutation occurs on search request completion */}
			<UFOInteractionIgnore>
				<SearchPerformedEventEmitter
					cloudId={cloudId}
					setHandleSearchCompletedRef={setHandleSearchCompletedRef}
				/>
			</UFOInteractionIgnore>
			<QuickFindInternal
				cloudId={cloudId}
				orgId={orgId}
				primaryProduct="confluence"
				analyticsAttributes={analyticsAttributes}
				isAdminHubAIEnabled={isAdminHubAIEnabled}
				isNav4Enabled
				onNavigate={onNavigate}
				queryParams={queryParams}
				open={open}
				setOpen={setOpen}
				user={user}
				generateSearchUrl={onGenerateSearchUrlWithPreload}
				generateUserUrl={onGenerateUserUrl}
				generateTeamUrl={onGenerateTeamUrl}
				experimentLayers={experimentLayers}
				isRovoLLMEnabled={isRovoLLMEnabled}
				isSSR={renderServerPlaceholder}
				customPlaceholder={persistedGhostText}
				onGhostTextGenerated={onPersistGhostText}
				inputRef={inputRef}
				callbacks={callbacks}
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? NAVIGATION_SHORTCUT : undefined}
				accelerator={
					isLiveEditMode || isOnEditRoute
						? universalNavigationShortcut
						: [universalNavigationShortcut, NAVIGATION_SLASH_SHORTCUT]
				}
				listener={onOpenQuickFind}
				isAppNavigationShortcut
			/>
		</>
	);
};
